import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader, StackedList, Well } from "../ui/Field";

type Domain = { name: string; status: string; verification_data?: { error_message?: string } };

export default function Domains() {
  const { slug } = useParams();
  const [items, setItems] = useState<Domain[]>([]);
  const [primary, setPrimary] = useState<string>("");
  const [newDomain, setNewDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<{ name: string; cname_target: string } | null>(null);

  const load = () => api.get<{ items: Domain[]; primary_domain: string | null; error?: string }>(`/api/sites/${slug}/domains`)
    .then((r) => { setItems(r.items); setPrimary(r.primary_domain ?? ""); if (r.error) setErr(r.error); })
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setInstructions(null);
    try {
      const r = await api.post<{ name: string; cname_target: string }>(`/api/sites/${slug}/domains`, { name: newDomain });
      setInstructions({ name: r.name, cname_target: r.cname_target });
      setNewDomain("");
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove(name: string) {
    if (!confirm(`Detach ${name}?`)) return;
    try { await api.delete(`/api/sites/${slug}/domains/${name}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }
  async function savePrimary() {
    try { await api.put(`/api/sites/${slug}/primary-domain`, { primary_domain: primary || null }); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 10 · Domains"
        title={<>Custom <em>domains</em></>}
        description="Attach any domain you control. Cloudflare verifies via CNAME and provisions TLS automatically."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="D1" title="Attach domain">
        <form onSubmit={add} className="grid grid-cols-[1fr_120px] gap-3 items-end">
          <Field label="Domain name" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="www.example.com" required className="mb-0" />
          <Button type="submit" disabled={busy} className="h-9">{busy ? "Adding…" : "Attach"}</Button>
        </form>
        {instructions && (
          <Banner kind="info" title="One DNS record away">
            <div>Add this record at your DNS provider for <Mono>{instructions.name}</Mono>:</div>
            <pre className="bg-[var(--color-surface)] mt-2 px-3 py-2 rounded-[var(--radius-sm)] text-[12px] font-mono overflow-x-auto">CNAME  {instructions.name}  →  {instructions.cname_target}</pre>
            <div className="mt-2 text-[12px]">Cloudflare will issue a TLS certificate and flip status from <Mono>pending</Mono> to <Mono>active</Mono>.</div>
          </Banner>
        )}
        <p className="text-[12px] text-[var(--color-muted)] mt-3">Publish the site at least once before attaching a domain — the Pages project must exist.</p>
      </Card>

      <Card number="D2" title="Primary domain" description="Used to build canonical URLs in the published HTML." className="mt-6">
        <div className="flex gap-3">
          <input value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="example.com" className="flex-1 h-9 px-3 text-[14px] border border-[var(--color-rule)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-ink)]" />
          <Button onClick={savePrimary}>Save</Button>
        </div>
      </Card>

      <Card number="D3" title={`Attached (${items.length})`} className="mt-6" bodyClassName="p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No domains attached." description={<>The site is served from <Mono>{`<slug>`}.pages.dev</Mono> by default.</>} /></div>
        ) : (
          <StackedList className="px-5">
            {items.map((d) => (
              <li key={d.name} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] text-[var(--color-ink)] font-medium">{d.name}</span>
                    <Badge tone={d.status === "active" ? "success" : "warning"}>{d.status}</Badge>
                  </div>
                  {d.verification_data?.error_message && (
                    <div className="text-[11.5px] text-[var(--color-rust)] mt-0.5">{d.verification_data.error_message}</div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(d.name)}>Detach</Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
